import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ClipboardList, Play, FileText, Loader2, Building2, AlertTriangle,
  TrendingUp, TrendingDown, Sparkles, ChevronRight, ShieldCheck, Calendar,
  Check as CheckIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { EvidenceTrigger } from "@/components/evidence/EvidencePanel";
import { SURVEY } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api, { getAccessToken } from "@/lib/api";

const SEMA_PRIMARY = "#1F3A93";
const RAW = process.env.REACT_APP_BACKEND_URL;

function ReadinessRing({ pct }) {
  const clean = Math.max(0, Math.min(100, Number(pct) || 0));
  const r = 42;
  const c = 2 * Math.PI * r;
  const off = c * (1 - clean / 100);
  const color = clean >= 90 ? "#10b981" : clean >= 70 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative inline-flex h-28 w-28 items-center justify-center">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth="8" />
        <circle
          cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span data-testid={SURVEY.readinessPct} className="text-2xl font-semibold">
          {clean.toFixed(0)}%
        </span>
        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Ready</span>
      </div>
    </div>
  );
}

function Sparkline({ points }) {
  const width = 320;
  const height = 60;
  if (!points || points.length === 0) {
    return <div className="text-xs text-muted-foreground">No history yet.</div>;
  }
  const values = points.map((p) => Number(p.readiness_pct) || 0);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 100);
  const range = max - min || 1;
  const step = width / Math.max(1, points.length - 1);
  const path = values
    .map((v, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(height - ((v - min) / range) * height).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={width} height={height} data-testid={SURVEY.trendChart}>
      <path d={path} fill="none" stroke={SEMA_PRIMARY} strokeWidth="2" />
      {values.map((v, i) => (
        <circle
          key={i}
          cx={i * step}
          cy={height - ((v - min) / range) * height}
          r="2"
          fill={SEMA_PRIMARY}
        />
      ))}
    </svg>
  );
}

async function downloadAuthed(url, filename) {
  const token = getAccessToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

export default function SurveyReadiness() {
  const { active } = useWorkspace();
  const workspaceId = active?.id;
  const [sets, setSets] = useState([]);
  const [setId, setSetId] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [seeding, setSeeding] = useState(null);
  const [packs, setPacks] = useState([]);

  const loadLibrary = async () => {
    if (!workspaceId) return;
    const { data } = await api.get(`/checks/library`, { params: { workspace_id: workspaceId } });
    setSets(data.sets || []);
    if (!setId && (data.sets || []).length > 0) {
      setSetId(data.sets[0].id);
    }
  };

  const loadDashboard = async (sid) => {
    if (!sid || !workspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/survey-readiness/dashboard`, {
        params: { workspace_id: workspaceId, check_set_id: sid },
      });
      setDashboard(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load readiness.");
    } finally {
      setLoading(false);
    }
  };

  const loadSchedules = async () => {
    if (!workspaceId) return;
    try {
      const { data } = await api.get(`/survey-readiness/schedules`, {
        params: { workspace_id: workspaceId },
      });
      setSchedules(data);
    } catch {
      /* non-fatal */
    }
  };

  useEffect(() => { loadLibrary(); loadSchedules(); }, [workspaceId]);
  useEffect(() => { if (setId) loadDashboard(setId); }, [setId, workspaceId]);

  useEffect(() => {
    let cancelled = false;
    api.get("/checks/packs").then(({ data }) => {
      if (!cancelled) setPacks(data.packs || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const installedSlugs = useMemo(() => new Set(sets.map((s) => s.slug).filter(Boolean)), [sets]);

  const seed = async (packKey) => {
    setSeeding(packKey);
    try {
      await api.post(`/checks/seed/${packKey}`, null, { params: { workspace_id: workspaceId } });
      toast.success(`Installed pack: ${packKey}`);
      await loadLibrary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed.");
    } finally {
      setSeeding(null);
    }
  };

  const runNow = async () => {
    if (!setId) return;
    setRunning(true);
    try {
      const { data } = await api.post(
        `/survey-readiness/run-now`,
        null,
        { params: { workspace_id: workspaceId, check_set_id: setId } },
      );
      toast.success(`Readiness: ${data.readiness_pct}%`);
      loadDashboard(setId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed.");
    } finally {
      setRunning(false);
    }
  };

  const scheduleDaily = async () => {
    try {
      await api.post(`/survey-readiness/schedules`, {
        workspace_id: workspaceId,
        check_set_id: setId,
        cron: "0 6 * * *",
        timezone: "UTC",
        alert_threshold_pct: 90,
      });
      toast.success("Scheduled daily at 6am UTC.");
      loadSchedules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule.");
    }
  };

  const exportPacket = async () => {
    if (!dashboard?.latest?.id) return;
    try {
      await downloadAuthed(
        `${RAW}/api/survey-readiness/snapshots/${dashboard.latest.id}/packet.pdf`,
        `survey-packet-${dashboard.latest.id}.pdf`,
      );
      toast.success("Downloaded audit packet.");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const latest = dashboard?.latest;
  const trend = dashboard?.trend || [];

  const delta = useMemo(() => {
    if (trend.length < 2) return null;
    const cur = trend[trend.length - 1].readiness_pct || 0;
    const prev = trend[trend.length - 2].readiness_pct || 0;
    return Number((cur - prev).toFixed(1));
  }, [trend]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={SURVEY.root}>
      <WorkspaceNav />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div
              className="mb-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em]"
              style={{ color: SEMA_PRIMARY }}
            >
              <ClipboardList className="h-3.5 w-3.5" /> Audit-Readiness Agent
            </div>
            <h1 className="text-3xl font-semibold">Audit Readiness</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Scheduled governed checks, per-facility drill-down, and one-click audit packets.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              data-testid={SURVEY.setSelect}
              value={setId}
              onChange={(e) => setSetId(e.target.value)}
              className="h-9 min-w-[14rem] rounded-full border border-border/70 bg-background px-3 text-xs"
            >
              <option value="">— pick a Check Set —</option>
              {sets.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {sets.length === 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={!!seeding} className="rounded-full text-xs">
                    {seeding ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Seed pack
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    Bundled Check Sets
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {packs.map((p) => {
                    const installed = installedSlugs.has(p.slug);
                    return (
                      <DropdownMenuItem
                        key={p.key}
                        onSelect={(e) => { e.preventDefault(); if (!installed) seed(p.key); }}
                        disabled={installed}
                      >
                        <div className="flex w-full items-center gap-2">
                          <span className="text-xs">{p.name}</span>
                          <span className="ml-auto text-[10px] text-muted-foreground">
                            {p.num_checks}
                          </span>
                          {installed && <CheckIcon className="h-3 w-3 text-emerald-500" />}
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button
              data-testid={SURVEY.runNow}
              size="sm"
              onClick={runNow}
              disabled={!setId || running}
              className="rounded-full text-xs text-white hover:opacity-95"
              style={{ background: SEMA_PRIMARY }}
            >
              {running ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
              Run now
            </Button>
            <Button
              data-testid={SURVEY.scheduleBtn}
              size="sm"
              variant="outline"
              onClick={scheduleDaily}
              disabled={!setId}
              className="rounded-full text-xs"
            >
              <Calendar className="mr-1.5 h-3.5 w-3.5" /> Schedule daily
            </Button>
            <Button
              data-testid={SURVEY.packetBtn}
              size="sm"
              variant="outline"
              onClick={exportPacket}
              disabled={!latest}
              className="rounded-full text-xs"
              style={{ borderColor: "rgba(31,58,147,0.4)", color: SEMA_PRIMARY }}
            >
              <FileText className="mr-1.5 h-3.5 w-3.5" /> Generate Audit Packet
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading readiness…
          </div>
        ) : !latest ? (
          <div className="mt-16 rounded-2xl border border-dashed border-border/60 p-10 text-center">
            <ShieldCheck className="mx-auto mb-3 h-8 w-8" style={{ color: SEMA_PRIMARY }} />
            <p className="text-sm text-foreground/90">No readiness snapshot yet.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Pick a Check Set and press <em>Run now</em>, or schedule daily runs.
            </p>
            <div className="mt-4 text-xs text-muted-foreground">
              Or <Link to="/checks" className="underline">manage the Check Library</Link>.
            </div>
          </div>
        ) : (
          <>
            <section className="mt-6 grid gap-4 md:grid-cols-3">
              <div
                className="flex items-center gap-4 rounded-2xl border p-5"
                style={{ borderColor: "rgba(31,58,147,0.35)", background: "rgba(31,58,147,0.04)" }}
              >
                <ReadinessRing pct={latest.readiness_pct} />
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Overall</div>
                  <div className="mt-0.5 text-sm">
                    {latest.passing_checks}/{latest.total_checks} checks passing
                  </div>
                  {delta !== null && (
                    <div className={`mt-1 flex items-center gap-1 text-[11px] ${delta >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                      {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {delta >= 0 ? "+" : ""}{delta}% vs prior run
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-border/60 p-5 md:col-span-2">
                <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                  Readiness trend
                </div>
                <Sparkline points={trend} />
                <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>{trend[0]?.ran_at?.split("T")[0]}</span>
                  <span>{trend[trend.length - 1]?.ran_at?.split("T")[0]}</span>
                </div>
              </div>
            </section>

            <section className="mt-8">
              <h2 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em]" style={{ color: SEMA_PRIMARY }}>
                <Building2 className="h-3.5 w-3.5" /> Facility drill-down
              </h2>
              {(latest.facilities || []).length === 0 ? (
                <div className="rounded-lg border border-border/50 p-6 text-sm text-emerald-500">
                  All facilities passing every check.
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {(latest.facilities || []).map((f) => (
                    <div
                      key={f.facility_id}
                      data-testid={SURVEY.facilityRow}
                      className="rounded-2xl border border-border/60 bg-background/50 p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{f.facility_name || `Facility ${f.facility_id}`}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {f.checks_failed} check(s) failing · {f.findings} finding(s)
                          </div>
                        </div>
                        <Badge
                          variant="outline"
                          className={`rounded-full font-mono text-[10px] ${
                            f.readiness_pct >= 90
                              ? "border-emerald-500/40 text-emerald-500"
                              : f.readiness_pct >= 70
                              ? "border-amber-500/40 text-amber-500"
                              : "border-red-500/40 text-red-500"
                          }`}
                        >
                          {f.readiness_pct}%
                        </Badge>
                      </div>
                      <ul className="mt-3 space-y-1 border-t border-border/40 pt-3">
                        {(f.records || []).slice(0, 6).map((r, i) => (
                          <li
                            key={i}
                            data-testid={SURVEY.findingRow}
                            className="flex items-center gap-2 text-[11px]"
                          >
                            <Badge variant="outline" className="rounded-full text-[9px]">
                              {r.citation}
                            </Badge>
                            <span className="flex-1 truncate text-muted-foreground">
                              {Object.entries(r.record || {}).slice(0, 3).map(([k, v]) => `${k}=${v}`).join(", ")}
                            </span>
                            <EvidenceTrigger answerId={r.evidence_answer_id} label="Evidence" />
                          </li>
                        ))}
                        {(f.records || []).length > 6 && (
                          <li className="text-[10px] text-muted-foreground">
                            +{f.records.length - 6} more finding(s)
                          </li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-8">
              <h2 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em]" style={{ color: SEMA_PRIMARY }}>
                <AlertTriangle className="h-3.5 w-3.5" /> Grouped by citation
              </h2>
              <div className="space-y-2">
                {(latest.by_citation || []).map((c) => (
                  <div
                    key={c.citation}
                    data-testid={SURVEY.citationRow}
                    className="flex items-center justify-between rounded-lg border border-border/50 p-3"
                  >
                    <div>
                      <div className="font-mono text-xs" style={{ color: SEMA_PRIMARY }}>
                        {c.citation}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {c.findings} finding(s) across {(c.checks || []).length} check(s)
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </section>

            {schedules.length > 0 && (
              <section className="mt-8 rounded-2xl border border-border/60 p-4">
                <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                  Active schedules
                </div>
                <ul className="space-y-1">
                  {schedules.map((s) => (
                    <li key={s.id} className="flex items-center gap-2 text-xs">
                      <Calendar className="h-3.5 w-3.5" style={{ color: SEMA_PRIMARY }} />
                      <span className="font-mono">{s.cron}</span>
                      <span className="text-muted-foreground">({s.timezone})</span>
                      <span className="ml-auto text-muted-foreground">
                        next: {s.next_run_at?.split("T")[0]}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
